import * as React from 'react';
import { AddDocumentItemOptions } from 'app/infrastructure/ui/form/types';
import { ModalWrapper, ModalWrapperProps } from 'app/components/modal-wrapper/modal-wrapper';
import { ComplexValue, LoadOptions, DisplayCodes } from 'ui';
import { SelectMultipleHierarchy } from 'app/area/shared/translated-components';
import { isNumeric } from 'app/infrastructure/data/number';
import { Translations } from 'ui/common/select/types';
import * as navigate from 'app/area/shared/navigation';
import { ManageOptionsParams } from 'app/area/case/selectors';

export interface MultiSelectReferenceModalTranslations extends Translations {
  textTitle: string;
  textPlaceholderSearch: string;
}

export interface MultiSelectReferenceModalProps extends ModalWrapperProps, MultiSelectReferenceModalTranslations {
  multipleSelectReferencesToBeAdded: ComplexValue<number>[];
  fetchingModalOptions: boolean;
  referenceModalOptions: ComplexValue<number>[];
  onLoadOptions: (loadOptions: LoadOptions) => void;
  onAddDocumentItem: (selectAddedItem?: boolean, options?: AddDocumentItemOptions) => void;
  referenceCodeDataTag: string;
  referenceOptionDataTag: string;
  textManageOptions?: string;
  manageOptions?: ManageOptionsParams;
  referenceOptionCodetableId?: string;
  locale: string;
  children?: React.ReactNode;
}

export interface MultiSelectReferenceModalState {
  fetching: boolean;
  selectedOptionsPerReference: { [reference: number]: ComplexValue<number>[] };
  mostRecentLoadOptionsPerReference: { [reference: number]: LoadOptions };
}

export class MultiSelectReferenceModal extends React.PureComponent<MultiSelectReferenceModalProps, MultiSelectReferenceModalState> {
  public state: Readonly<MultiSelectReferenceModalState> = {
    fetching: false,
    selectedOptionsPerReference: {},
    mostRecentLoadOptionsPerReference: {}
  };

  private onDone = () => {

    const { multipleSelectReferencesToBeAdded, referenceCodeDataTag, referenceOptionDataTag } = this.props;

    if (!referenceCodeDataTag || !referenceOptionDataTag) {
      return;
    }

    for (const reference of multipleSelectReferencesToBeAdded) {
      const selectedOptions = this.state.selectedOptionsPerReference[reference.value];

      if (!selectedOptions) {
        continue;
      }

      for (const option of selectedOptions) {

        this.props.onAddDocumentItem(false,
          {
            initialValues: {
              [referenceCodeDataTag]: [reference],
              [referenceOptionDataTag]: [option]
            }
          }
        );
      }
    }
  };

  private onLoadOptions = (loadOptions: LoadOptions, referenceId: number) => {
    // This is a hack to support hiearchical selectors.
    // It would be much cleaner if the "options" prop included the options used to fetch it.
    this.setState({
      ...this.state,
      mostRecentLoadOptionsPerReference: {
        ...this.state.mostRecentLoadOptionsPerReference,
        [referenceId]: loadOptions
      }
    });
    // This hardcoded domain is to get reference options.
    this.props.onLoadOptions({...loadOptions, domainId: 11938, categoryId: referenceId});
  };

  private onSelectedOptionsChanged = (options: ComplexValue<number>[], referenceId: number) => {
    this.setState({
      ...this.state,
      selectedOptionsPerReference: {
        ...this.state.selectedOptionsPerReference,
        [referenceId]: options
      }
    });
  };

  private createOnChange = (referenceId: number) => values => this.onSelectedOptionsChanged(values, referenceId);

  private createLoadOptions = (referenceId: number) => (loadOptions: LoadOptions) => this.onLoadOptions(loadOptions, referenceId);

  private renderReferenceOptionsSelector(reference: ComplexValue<number>) {
    const {
      textPlaceholderSearch,
      fetchingModalOptions,
      referenceModalOptions,
      locale
    } = this.props;
    const { selectedOptionsPerReference, mostRecentLoadOptionsPerReference } = this.state;
    const referenceId = reference.value;

    return <div key={`${referenceId}-wrapper`}>
      <DisplayCodes {...({componentId: `${referenceId}-code`, className: 'display-codes-read', valueItems: [reference] } as any)} />
      <SelectMultipleHierarchy
        componentId={`${referenceId}-selector`} className={undefined}
        valueItems={selectedOptionsPerReference[reference.value] || []}
        options={referenceModalOptions || []}
        fetching={fetchingModalOptions}
        onChange={this.createOnChange(referenceId)}
        onLoadOptions={this.createLoadOptions(referenceId)}
        loadOptions={mostRecentLoadOptionsPerReference[referenceId]}
        placeholder={textPlaceholderSearch}
        locale={locale}
      />
    </div>;
  }

  private navigateToManageReferenceOptions = () => navigate.toCodeList(undefined, Number(this.props.referenceOptionCodetableId));

  public render() {
    const {
      multipleSelectReferencesToBeAdded,
      textTitle,
      textOptionsDone
    } = this.props;

    const referenceSelectors = multipleSelectReferencesToBeAdded &&
      multipleSelectReferencesToBeAdded
        .filter(r => isNumeric(r.value))
        .map(r => this.renderReferenceOptionsSelector(r));

    const canManageOptions = this.props.manageOptions?.create && this.props.referenceOptionCodetableId;

    return (
      <ModalWrapper {...this.props}
        headerTitle={textTitle}
        btnTitle={textOptionsDone}
        onDone={this.onDone}
        onExtraFooterAction={canManageOptions && this.navigateToManageReferenceOptions}
        btnExtraFooterActionTitle={this.props.textManageOptions}
      >
        {referenceSelectors}
      </ModalWrapper>
    );
  }
}
